import { getNamedToolSchema } from '@takeshape/schema/project-schema/latest.js';
import type { FieldConfig, FormConfig, Validator, WidgetRegistry } from '@takeshape/shape-form';
import { defaultWidgets } from '@takeshape/shape-form/widgets';
import { type GraphQLSchema, parse, validate } from 'graphql';
import type { JSONSchema7 } from 'json-schema';
import cloneDeep from 'lodash/cloneDeep.js';
import type { MutableRefObject } from 'react';
import type { ToolEditorFormState } from '../../types.js';
import ToolQueryEditorWidget from './tool-query-editor/index.tsx';

const baseToolSchema = getNamedToolSchema();

export const toolEditorSchema: JSONSchema7 = {
  ...cloneDeep(baseToolSchema),
  definitions: {
    ...baseToolSchema.definitions,

    toolExecutionGraphQLOptimizeResponse: {
      title: 'ToolExecutionGraphQLOptimizeResponseJSON',
      type: 'object',
      properties: {
        compactPagination: {
          type: 'boolean',
          title: 'Compact Pagination',
          description: 'Simplify relay pagination responses to reduce token usage'
        }
      },
      additionalProperties: false
    },

    toolExecution: {
      title: 'ToolExecutionGraphQLJSON',
      type: 'object',
      properties: {
        type: {
          type: 'string'
        },
        query: {
          type: 'string'
        },
        path: {
          type: 'string',
          title: 'Path',
          description: 'Extract a nested path from the GraphQL result (e.g., "getProducts.items")'
        },
        optimizeResponse: {
          $ref: '#/definitions/toolExecutionGraphQLOptimizeResponse'
        }
      },
      additionalProperties: false,
      required: ['query']
    }
  }
} as JSONSchema7;

/** Property order for the root tool object */
export const toolEditorFieldOrder = ['name', 'visibility', 'description', 'execution'];

/**
 * Store-level config — uses path-based keys for field ordering and widget assignment.
 */
export const toolEditorFormConfig: FormConfig = {
  name: { title: 'Name', widget: 'text', order: 0 },
  visibility: {
    title: 'Visibility',
    widget: 'select',
    description: '"public" tools will be made available via MCP',
    order: 1
  },
  description: {
    title: 'Description',
    widget: 'paragraphText',
    description: 'Used by the language model to determine whether to use the tool.',
    order: 2
  },
  'execution.query': { widget: 'toolQueryEditor', title: 'Query' },
  'execution.path': {
    widget: 'text',
    title: 'Path',
    description: 'Extract a nested path from result.data (e.g., "getProducts.items").'
  },
  'execution.optimizeResponse.compactPagination': {
    widget: 'checkbox',
    title: 'Compact Pagination',
    description: 'Simplify relay pagination responses (edges/node/pageInfo) to reduce token usage'
  }
};

/**
 * Config for the execution object field — passed as a prop to ObjectField.
 * Uses nested properties structure with order/exclude.
 */
export const executionFieldConfig: FieldConfig = {
  title: '',
  exclude: ['type'],
  order: ['query', 'path', 'optimizeResponse'],
  properties: {
    optimizeResponse: {
      title: 'Response Optimization'
    }
  }
};

export function createToolEditorValidator(
  toolName?: string,
  existingToolNames: string[] = [],
  graphqlSchemaRef?: MutableRefObject<GraphQLSchema | null>
): Validator<ToolEditorFormState> {
  return (values: ToolEditorFormState) => {
    const errors: Record<string, string> = {};

    if (values.name && values.name !== toolName && existingToolNames.includes(values.name)) {
      errors.name = 'Tool name must be unique.';
    }

    const queryText = values.execution?.query?.trim();
    if (!queryText) {
      errors['execution.query'] = 'A query or mutation is required.';
    } else {
      try {
        const doc = parse(queryText);
        const schema = graphqlSchemaRef?.current;
        if (schema) {
          const validationErrors = validate(schema, doc);
          if (validationErrors.length > 0) {
            errors['execution.query'] = 'Query has validation errors.';
          }
        }
      } catch {
        errors['execution.query'] = 'Query has syntax errors.';
      }
    }

    return errors;
  };
}

export const toolEditorWidgets: WidgetRegistry = {
  ...defaultWidgets,
  paragraphText: defaultWidgets.textarea,
  toolQueryEditor: ToolQueryEditorWidget
};
